// ───────────────────────────────────────────────────────────────────────────
// Demo core: demo mode flag + session seeding
// The flag lives in sessionStorage so it is scoped to one browser tab and never
// leaks into a real authenticated session in another tab. All four apps keep
// access_token / refresh_token / selectedOrganization in sessionStorage too, so
// the demo session survives the window.location.reload() the org switcher does.
// ───────────────────────────────────────────────────────────────────────────

import { DEMO_FLAG_KEY, DEMO_OUTLET_ORG_ID, DEMO_PERSONA_KEY, DEMO_RECYCLER_ORG_ID } from './constants';
import { buildFakeJwt, isFakeJwt } from './fakeJwt';

export type DemoPersona = 'outlet' | 'recycler';

export interface DemoSeedOrganization {
  id: string;
  name: string;
  organization_type: string;
  data?: Record<string, unknown>;
  logo_url?: string | null;
  [k: string]: unknown;
}

export const ACCESS_TOKEN_KEY = 'access_token';
export const REFRESH_TOKEN_KEY = 'refresh_token';
export const SELECTED_ORG_KEY = 'selectedOrganization';

/** Session keys owned by a demo session (besides the flag and persona). `language` is kept on purpose. */
export const DEMO_SESSION_KEYS: readonly string[] = [
  ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  SELECTED_ORG_KEY,
  'dashboardPreferences',
  'zeroHelpGuideBypass',
  'esgHelpGuideBypass',
  'marketHelpGuideBypass',
];

/** Per-session UI state wiped when a demo session starts so a previous session cannot bleed in. */
const DEMO_STALE_UI_KEYS: readonly string[] = DEMO_SESSION_KEYS.filter(
  (k) => k !== ACCESS_TOKEN_KEY && k !== REFRESH_TOKEN_KEY && k !== SELECTED_ORG_KEY,
);

function storage(): Storage | null {
  try {
    return typeof globalThis.sessionStorage === 'undefined' ? null : globalThis.sessionStorage;
  } catch {
    return null;
  }
}

function read(key: string): string | null {
  try {
    return storage()?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    storage()?.setItem(key, value);
  } catch {
    /* ignore: storage full or blocked */
  }
}

function remove(key: string): void {
  try {
    storage()?.removeItem(key);
  } catch {
    /* ignore */
  }
}

export function isDemoPersona(value: unknown): value is DemoPersona {
  return value === 'outlet' || value === 'recycler';
}

export function orgIdForPersona(persona: DemoPersona): string {
  return persona === 'outlet' ? DEMO_OUTLET_ORG_ID : DEMO_RECYCLER_ORG_ID;
}

export function personaForOrgId(orgId: string | null | undefined): DemoPersona | null {
  if (orgId === DEMO_OUTLET_ORG_ID) return 'outlet';
  if (orgId === DEMO_RECYCLER_ORG_ID) return 'recycler';
  return null;
}

export function isDemoMode(): boolean {
  return read(DEMO_FLAG_KEY) === 'true';
}

/**
 * Demo mode latched when this document loaded. Exiting a demo clears the flag and
 * then hard-navigates to /login; a request fired in between (a poll or a
 * react-query refetch) must still be answered by the fake API, never the real one.
 * A real tab (no flag at load) is unaffected: this stays false for its lifetime.
 */
export const BOOTED_IN_DEMO: boolean = isDemoMode();

/** True when this document booted as a demo tab or the tab is flagged as one now. */
export const demoActive = (): boolean => BOOTED_IN_DEMO || isDemoMode();

/** True on the public /demo and /demo/* entry routes (before the demo session is seeded). */
export const isDemoEntryPath = (pathname?: string): boolean => {
  try {
    const path = pathname ?? (typeof window === 'undefined' ? '' : window.location.pathname);
    return /^\/demo(\/|$)/.test(path);
  } catch {
    return false;
  }
};

/** Demo traffic for analytics / push guards: a flagged demo tab, or the demo entry page itself. */
export const isDemoTraffic = (): boolean => {
  try {
    return isDemoMode() || isDemoEntryPath();
  } catch {
    return false;
  }
};

/**
 * Current demo persona, or null outside demo mode. Derived from the selected
 * organization first (switching org in the sidebar IS the persona switch), then
 * from the persona the demo was opened with.
 */
export function getDemoPersona(): DemoPersona | null {
  if (!isDemoMode()) return null;
  const raw = read(SELECTED_ORG_KEY);
  if (raw) {
    try {
      const fromOrg = personaForOrgId((JSON.parse(raw) as { id?: unknown } | null)?.id as string | undefined);
      if (fromOrg) return fromOrg;
    } catch {
      /* fall through */
    }
  }
  const stored = read(DEMO_PERSONA_KEY);
  return isDemoPersona(stored) ? stored : null;
}

/**
 * Start a demo session in this tab: flag, persona, fake access + refresh token
 * and the selected organization. Stale per-session UI state is cleared first.
 */
export function seedDemoSession(persona: DemoPersona, org: DemoSeedOrganization): void {
  DEMO_STALE_UI_KEYS.forEach(remove);
  const token = buildFakeJwt();
  write(DEMO_FLAG_KEY, 'true');
  write(DEMO_PERSONA_KEY, persona);
  write(ACCESS_TOKEN_KEY, token);
  write(REFRESH_TOKEN_KEY, token);
  write(SELECTED_ORG_KEY, JSON.stringify(org));
}

/**
 * End the demo session: removes the flag and persona and, when this tab holds a
 * demo session (flag set or a fake token), the tokens, selected organization,
 * dashboard preferences and help-guide bypass flags. `language` is kept.
 * Safe to call anywhere (login, logout, Login page mount): it never touches a
 * real session's tokens.
 */
export function clearDemoSession(): void {
  const wasDemo = isDemoMode() || isFakeJwt(read(ACCESS_TOKEN_KEY)) || isFakeJwt(read(REFRESH_TOKEN_KEY));
  remove(DEMO_FLAG_KEY);
  remove(DEMO_PERSONA_KEY);
  if (wasDemo) DEMO_SESSION_KEYS.forEach(remove);
}

/** Clear the demo session and go to the login page. */
export function exitDemo(): void {
  clearDemoSession();
  try {
    if (typeof window !== 'undefined') window.location.href = '/login';
  } catch {
    /* ignore */
  }
}
